import { useId } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { SelectOption } from './Select';

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly SelectOption[];
  /** Label of the "no filter" option, e.g. "All countries". */
  allLabel: string;
  className?: string;
  disabled?: boolean;
  /** Tooltip, e.g. why the filter is unavailable. */
  title?: string;
}

/** Compact filter control. The visible prefix shows the filter name so the current value is always understandable. */
export function FilterSelect({ label, value, onChange, options, allLabel, className, disabled = false, title }: FilterSelectProps) {
  const id = useId();
  const active = value !== '';
  return (
    <div className={cn('relative', className)} title={title}>
      <label htmlFor={id} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-ink-muted">
        {label}:
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cn(
          'control h-control appearance-none pr-9 font-medium',
          disabled && 'cursor-not-allowed opacity-60',
          active && 'border-primary/50 bg-primary-soft/40 text-primary',
        )}
        style={{ paddingLeft: `${label.length * 7 + 22}px` }}
      >
        <option value="">{allLabel}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown aria-hidden="true" size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted" />
    </div>
  );
}
